// Anwendertaugliche Kurzfassung einer Gambio-API-Fehlermeldung.
//
// Die Edge Functions geben den Gambio-Antwortkörper unverändert weiter. In der Oberfläche stand
// dadurch die komplette JSON-Antwort, mehrfach übereinander, sobald ein Aufruf scheiterte
// (User-Befund 2026-08-21, drei Blöcke im Artikel-Slot bei ungültigen Zugangsdaten):
//
//   Kategorien nicht ladbar: gambio GET /categories?recursive=1&per_page=100&page=1 -> 401:
//   { "code": 401, "status": "error", "message": "Invalid Credentials", "request": { … } }
//
// Für den Anwender ist daran nur eine Information brauchbar: die Zugangsdaten stimmen nicht.
// `describeGambioError` macht daraus einen Satz. Der technische Wortlaut geht NICHT verloren, er
// gehört ins Fehler-Modal bzw. ins Protokoll — nur nicht in die Fläche.

export interface GambioErrorDescription {
  /** Kurzer Satz für die Oberfläche. */
  text: string;
  /** true = Zugangsdaten-/Rechteproblem. Der Aufrufer kann dann auf die Einstellungen verweisen. */
  isAuth: boolean;
}

/** Erste Zeile, JSON-Rumpf ab der ersten `{` abgeschnitten, auf `max` Zeichen begrenzt. */
function technicalHead(raw: string, max = 120): string {
  const firstLine = raw.split('\n')[0] ?? raw;
  const brace = firstLine.indexOf('{');
  const cut = (brace > 0 ? firstLine.slice(0, brace) : firstLine).trim().replace(/[:\s]+$/, '');
  return cut.length > max ? `${cut.slice(0, max - 1)}…` : cut;
}

/**
 * Übersetzt eine rohe Fehlermeldung aus einem Gambio-Aufruf in einen kurzen Satz.
 * Erkennungsmerkmale sind Statuscode und Gambio-Wortlaut, beides tolerant geprüft, weil die
 * Meldung je Endpunkt unterschiedlich verpackt ist (`-> 401`, `→ 401`, `INVALID_CREDENTIALS`).
 */
export function describeGambioError(raw: string | null | undefined): GambioErrorDescription {
  const s = (raw ?? '').trim();
  if (s.length === 0) return { text: 'Unbekannter Fehler.', isAuth: false };

  const has401 = /\b401\b/.test(s) || /invalid[_ ]credentials/i.test(s);
  if (has401) {
    return {
      text: 'Die Zugangsdaten für den Gambio-Shop werden abgelehnt. Bitte in den Einstellungen erneuern.',
      isAuth: true,
    };
  }
  const has403 = /\b403\b/.test(s) || /no permissions/i.test(s);
  if (has403) {
    return {
      text: 'Der Gambio-Benutzer ist angemeldet, hat aber keine REST-Berechtigungen. Im Gambio-Admin unter '
        + '„Kunden → Rollen und Berechtigungen" freigeben.',
      isAuth: true,
    };
  }
  if (/\b404\b/.test(s)) {
    return { text: 'Der Shop kennt diesen Datensatz nicht mehr (404).', isAuth: false };
  }
  if (/\b5\d\d\b/.test(s)) {
    return { text: `Der Shop hat mit einem Serverfehler geantwortet. ${technicalHead(s)}`.trim(), isAuth: false };
  }
  return { text: technicalHead(s, 180), isAuth: false };
}
